import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OdooActivityGame = ({ user }) => {
  const { activityId } = useParams();
  const navigate = useNavigate();
  
  const [activity, setActivity] = useState(null);
  const [container, setContainer] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');

  useEffect(() => {
    fetchActivity();
  }, [activityId]);

  useEffect(() => {
    if (countdown !== null && countdown > 0) {
      const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const fetchActivity = async () => {
    try {
      console.log('[DEBUG] Fetching activity:', activityId);
      const response = await axios.get(`${BACKEND_URL}/api/odoo-activities/${activityId}`);
      setActivity(response.data);
      
      // Check if student already has a container
      await checkExistingContainer();
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching activity:', err);
      setError('Error al cargar la actividad');
      setLoading(false);
    }
  };

  const checkExistingContainer = async () => {
    try {
      console.log('[DEBUG] Checking existing containers for student:', user.id);
      const response = await axios.get(
        `${BACKEND_URL}/api/student/${user.id}/odoo-containers`
      );
      
      const containers = response.data.containers || [];
      console.log('[DEBUG] Found containers:', containers);
      
      // Buscar contenedor para esta actividad o cualquier contenedor Odoo
      const existingContainer = containers.find(c => 
        c.activity_id === activityId || c.activity_id === ''
      ) || containers[0];
      
      if (existingContainer) {
        console.log('[DEBUG] Using existing container:', existingContainer);
        setContainer(existingContainer);
      }
    } catch (err) {
      console.log('[DEBUG] No existing container found:', err);
    }
  };

  const startContainer = async () => {
    setStarting(true);
    setError('');

    try {
      console.log('[DEBUG] Starting container for activity:', activityId);
      const response = await axios.post(
        `${BACKEND_URL}/api/odoo-activities/start-container`,
        {
          student_id: user.id,
          student_name: user.name,
          student_email: user.email,
          activity_id: activityId
        }
      );

      console.log('[DEBUG] Container started:', response.data);
      
      setContainer({
        container_id: response.data.container_id,
        host_port: response.data.host_port,
        status: 'running',
        web_url: response.data.url
      });
      setCountdown(10);
    } catch (err) {
      console.error('[DEBUG] Error starting container:', err);
      if (err.response?.status === 409) {
        setError('Ya tienes un contenedor Odoo activo. Espera un momento...');
        // Reintentar buscar el contenedor existente
        await checkExistingContainer();
      } else {
        setError(err.response?.data?.detail || 'Error al iniciar el contenedor');
      }
    } finally {
      setStarting(false);
    }
  };

  const verifyProgress = async () => {
    if (!container?.container_id) return;
    
    setVerifying(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/odoo-activities/${activityId}/verify`,
        {
          student_id: user.id,
          container_id: container.container_id
        }
      );

      setResults(response.data.result);
    } catch (err) {
      console.error('Error verifying:', err);
      setError('Error al verificar progreso');
    } finally {
      setVerifying(false);
    }
  };

  const uploadModule = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setUploadMsg('');
    try {
      const formData = new FormData();
      formData.append('module_zip', file);
      const response = await axios.post(
        `${BACKEND_URL}/api/student/${user.id}/odoo-upload-module`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setUploadMsg(`✅ ${response.data.message}`);
    } catch (err) {
      setUploadMsg(`❌ ${err.response?.data?.detail || 'Error al instalar el módulo'}`);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const getOdooUrl = () => {
    if (container?.web_url) {
      return container.web_url;
    } else if (container?.host_port) {
      // Usar subdominio (nuevo sistema) - FORZAR RECOMPILACION
      const subdomain = "odoo-" + container.host_port + ".play.leaderdreams.com";
      return "https://" + subdomain + "/web";
    }
    return null;
  };

  const openOdoo = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    const url = getOdooUrl();
    console.log('[DEBUG] Opening Odoo URL:', url);
    
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      setError('No hay URL disponible. Intenta iniciar Odoo primero.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 flex items-center justify-center">
        <div className="text-white text-xl">Cargando actividad...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 p-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 mb-6 border border-white/20">
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="text-4xl">🏢</span>
                <h1 className="text-2xl font-bold text-white">{activity?.title}</h1>
              </div>
              <p className="text-white/70 max-w-2xl">{activity?.description}</p>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-xl transition-all"
            >
              ← Salir
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-500/20 border border-red-500/50 text-red-200 p-4 rounded-xl mb-4">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel */}
          <div className="lg:col-span-1 space-y-4">
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-5 border border-white/20">
              <h3 className="text-lg font-semibold text-white mb-3">📖 Instrucciones</h3>
              <ol className="text-white/80 text-sm space-y-2 list-decimal list-inside">
                <li>Pulsa "Iniciar Odoo" para crear tu instancia</li>
                <li>Espera ~10 segundos a que arranque</li>
                <li>Pulsa "Abrir Odoo" para acceder</li>
                <li>Completa los objetivos indicados</li>
                <li>Pulsa "Verificar progreso" para comprobar</li>
              </ol>
            </div>

            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-5 border border-white/20">
              <h3 className="text-lg font-semibold text-white mb-3">🎯 Objetivos</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {activity?.objectives?.map((obj, idx) => (
                  <div
                    key={obj.id || idx}
                    className={`p-3 rounded-lg text-sm ${
                      results?.objective_results?.find(r => r.objective_id === obj.id)?.passed
                        ? 'bg-green-500/20 border border-green-500/30'
                        : 'bg-white/5 border border-white/10'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <span className="text-white/90">{obj.name}</span>
                      <span className="text-white/60 text-xs">{obj.score} pts</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Center Panel */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
              <h3 className="text-lg font-semibold text-white mb-4">🚀 Estado de tu Instancia</h3>
              
              {!container ? (
                <div className="text-center py-8">
                  <div className="text-6xl mb-4">📦</div>
                  <p className="text-white/70 mb-4">Tu instancia Odoo aún no está creada</p>
                  <button
                    onClick={startContainer}
                    disabled={starting}
                    className="px-8 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold rounded-xl transition-all disabled:opacity-50"
                  >
                    {starting ? '⏳ Creando...' : '▶️ Iniciar Odoo'}
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between bg-white/5 p-4 rounded-xl">
                    <div>
                      <p className="text-white/60 text-sm">Estado</p>
                      <p className="font-semibold text-green-400">
                        🟢 {container.status === 'running' ? 'En ejecución' : container.status}
                      </p>
                    </div>
                    {countdown > 0 && (
                      <div className="text-center">
                        <p className="text-white/60 text-sm">Arrancando en</p>
                        <p className="text-2xl font-bold text-yellow-400">{countdown}s</p>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    {/* Botón Abrir Odoo - implementación simple */}
                    <a
                      href={container.web_url || `https://odoo-${container.host_port}.play.leaderdreams.com/web`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="py-3 bg-green-500/80 hover:bg-green-500 text-white font-semibold rounded-xl transition-all text-center block"
                    >
                      🌐 Abrir Odoo
                    </a>
                    
                    <button
                      onClick={verifyProgress}
                      disabled={verifying}
                      className="py-3 bg-blue-500/80 hover:bg-blue-500 text-white font-semibold rounded-xl transition-all disabled:opacity-50"
                    >
                      {verifying ? '⏳ Verificando...' : '✅ Verificar progreso'}
                    </button>
                  </div>

                  {/* Subir módulo ZIP */}
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <p className="text-white/70 text-sm mb-2">📦 Instalar módulo personalizado (.zip)</p>
                    <label className={`flex items-center justify-center gap-2 w-full py-2 rounded-lg cursor-pointer transition-all text-sm font-medium ${uploading ? 'bg-white/10 text-white/40 cursor-not-allowed' : 'bg-purple-500/70 hover:bg-purple-500 text-white'}`}>
                      {uploading ? '⏳ Instalando...' : '⬆️ Seleccionar ZIP'}
                      <input type="file" accept=".zip" className="hidden" disabled={uploading} onChange={uploadModule} />
                    </label>
                    {uploadMsg && (
                      <p className={`text-xs mt-2 ${uploadMsg.startsWith('✅') ? 'text-green-400' : 'text-red-400'}`}>{uploadMsg}</p>
                    )}
                  </div>

                  {container.host_port && (
                    <div className="bg-black/20 p-3 rounded-lg">
                      <p className="text-white/60 text-xs mb-1">URL directa:</p>
                      <code className="text-green-400 text-sm break-all">
                        https://odoo-{container.host_port}.play.leaderdreams.com/web
                      </code>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Results */}
            {results && (() => {
              const passed = results.objective_results?.filter(r => r.passed) || [];
              const failed = results.objective_results?.filter(r => !r.passed) || [];
              return (
                <div className={`rounded-2xl p-6 border ${
                  results.passed
                    ? 'bg-green-500/10 border-green-500/30'
                    : 'bg-yellow-500/10 border-yellow-500/30'
                }`}>
                  <h3 className="text-lg font-semibold text-white mb-4">
                    {results.passed ? '🎉 ¡Actividad Completada!' : '📊 Progreso'}
                  </h3>

                  {/* Score summary */}
                  <div className="flex items-center gap-6 mb-5">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-white">{results.score}<span className="text-lg text-white/50">/{results.max_score}</span></p>
                      <p className="text-white/60 text-sm">Puntos</p>
                    </div>
                    <div className="text-center">
                      <p className="text-3xl font-bold text-white">{results.percentage?.toFixed(1)}%</p>
                      <p className="text-white/60 text-sm">Completado</p>
                    </div>
                    <div className="text-center">
                      <p className="text-3xl font-bold text-green-400">{passed.length}<span className="text-lg text-white/50">/{results.objective_results?.length}</span></p>
                      <p className="text-white/60 text-sm">Objetivos</p>
                    </div>
                  </div>

                  {/* Passed objectives */}
                  {passed.length > 0 && (
                    <div className="mb-3">
                      <p className="text-green-400 text-sm font-semibold mb-2">✅ Superados ({passed.length})</p>
                      <div className="space-y-1">
                        {passed.map((r, i) => (
                          <div key={i} className="flex justify-between items-center bg-green-500/10 border border-green-500/20 rounded-lg px-3 py-2 text-sm">
                            <span className="text-green-300">{r.name}</span>
                            <span className="text-green-400 font-semibold">+{r.score} pts</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Failed objectives */}
                  {failed.length > 0 && (
                    <div>
                      <p className="text-red-400 text-sm font-semibold mb-2">❌ Pendientes ({failed.length})</p>
                      <div className="space-y-1">
                        {failed.map((r, i) => (
                          <div key={i} className="bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 text-sm">
                            <div className="flex justify-between items-center">
                              <span className="text-red-300">{r.name}</span>
                              <span className="text-white/40 text-xs">{r.score} pts</span>
                            </div>
                            {r.message && (
                              <p className="text-white/50 text-xs mt-1">{r.message.replace(/^❌\s*[^-]+-\s*/, '')}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OdooActivityGame;